import { Box, FlexBox, Typography, getTokenCssVar } from '@vp/swan';
import type { ReactNode, RefObject } from 'react';

export type SelectorCardProps = {
    /** The question text, e.g. "Which style speaks to you?" */
    title: string;
    /** The parenthetical hint after the title, e.g. "(Please select 1)". Omit for none. */
    subtitle?: ReactNode;
    /**
     * Focused on mount so screen readers announce the question immediately. Owned by the caller
     * (not created here) because the ref must be stable across the caller's own re-renders.
     */
    headingRef: RefObject<HTMLElement>;
    /** The scrollable option grid (SelectionSet + its tiles). */
    children: ReactNode;
    /** The Help/Continue button row. */
    footer: ReactNode;
};

/**
 * The shared card shell every composer selector renders into: background band, border, title
 * row, scrollable option area, and footer.
 *
 * Every descriptor's selector (style-tiles, color-selector, and any future type) renders this
 * exact shell so title spacing — `padding-bottom: var(--swan-sem-space-3)` in legacy — and the
 * card's background/border/radius can't drift between types by being copy-pasted per folder.
 * Only the option grid and footer buttons are type-specific; those stay in each descriptor's own
 * `SelectorView`.
 *
 * Renders only the card. Where that card sits — the composer's background band and centred
 * max-width column — belongs to whatever hosts the composer, exactly as it does for ChatInput.
 * A selector that positioned itself would have to agree with the host on those values, and would
 * double them up when mounted in the composer slot.
 */
export function SelectorCard({ title, subtitle, headingRef, children, footer }: SelectorCardProps) {
    return (
        <Box
            style={{
                backgroundColor: getTokenCssVar('SwanSemColorBgActive'),
                border: `1px solid ${getTokenCssVar('SwanSemColorBorderStandard')}`,
                padding: `${getTokenCssVar('SwanSemSpace6')} ${getTokenCssVar(
                    'SwanSemSpace6'
                )} ${getTokenCssVar('SwanSemSpace3')}`,
                borderRadius: `${getTokenCssVar('SwanSemBorderRadiusStandard')} ${getTokenCssVar(
                    'SwanSemBorderRadiusStandard'
                )} 0 0`,
            }}
        >
            <Box mb={4} mt={1}>
                <Typography
                    ref={headingRef}
                    tabIndex={-1}
                    fontSkin="title-item"
                    style={{
                        paddingBottom: getTokenCssVar('SwanSemSpace3'),
                        outline: 'none',
                        boxShadow: 'none',
                    }}
                >
                    {title}
                    {subtitle !== undefined && (
                        <>
                            {' '}
                            <Typography as="span" textColor="subtle" fontSkin="body-standard">
                                {subtitle}
                            </Typography>
                        </>
                    )}
                </Typography>
            </Box>
            {children}

            <FlexBox justifyContent="space-between" alignItems="center" gap={4}>
                {footer}
            </FlexBox>
        </Box>
    );
}
